import React, { useState, useEffect, useMemo } from 'react';
import {
  Avatar,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  Typography,
  Box,
  Chip,
  Select,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import {
  Brightness4,
  Brightness7,
  Logout,
  AltRoute,
  Merge,
  Language,
  SwapHoriz,
} from '@mui/icons-material';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import useShowOnlyProductiveTrips from 'hooks/useShowOnlyProductiveTrips';
import {
  convertUtcToDisplayTime,
  getCurrentUtcOffset,
  guessUserTimezone,
} from 'utils/helpers/dateTime';
import { useNavigate } from 'react-router-dom';
import useAppTheme from 'hooks/useAppTheme';
import ExternalUserHandler from 'handlers/external.user.handler';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { ReloadDialog } from 'components/common/ReloadDialog';
import { useCurrentUserInfo } from 'hooks/useCurrentUserInfo';
import { type User } from 'types/user.types';
import { isTpmsOnlyCustomer } from 'utils/helpers/general';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import { ThemePalette } from 'mui.theme';
import { useTranslation } from 'react-i18next';
import { setLanguage } from 'store/language.slice';
import type { SupportedLanguage } from 'i18n';
import { isExternalRole } from 'utils/validators/role.validation';

const UserInfoSection = ({
  user,
  initials,
}: {
  user: User | undefined;
  initials: string;
}) => {
  const { enableDarkTheme } = useAppTheme();
  const colorMode = enableDarkTheme ? 'dark' : 'light';
  const themeColors = clientThemePalette[CLIENT_THEME][colorMode];
  const { timezoneBasedOnDisplayMode, preferredTimeDisplayMode, timezone } =
    useTimeDisplayMode();
  const [timeInfo, setTimeInfo] = useState('');

  const resolvedTimeZoneOffset = useMemo(() => {
    const resolvedTimeZone = timezoneBasedOnDisplayMode || guessUserTimezone();
    const offsetMinutes = getCurrentUtcOffset(resolvedTimeZone);
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const hours = Math.floor(Math.abs(offsetMinutes) / 60)
      .toString()
      .padStart(2, '0');
    const minutes = (Math.abs(offsetMinutes) % 60).toString().padStart(2, '0');
    return `${resolvedTimeZone}(${sign}${hours}:${minutes})`;
  }, [timezoneBasedOnDisplayMode]);

  useEffect(() => {
    const updateTime = () => {
      setTimeInfo(
        convertUtcToDisplayTime(
          Date.now(),
          preferredTimeDisplayMode,
          timezone,
          'ddd, MMM D  hh:mm'
        )
      );
    };

    updateTime(); // on mount

    const interval = setInterval(updateTime, 1000 * 15);

    return () => {
      clearInterval(interval);
    };
  }, [preferredTimeDisplayMode, timezone]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 2,
        py: 2,
        borderRadius: 2,
        mb: 2,
        gap: 2,
        background: enableDarkTheme
          ? 'rgba(255,255,255,0.07)'
          : `${themeColors.primary}0d`,
        border: `1px solid ${themeColors.borderGrey}`,
      }}
    >
      <Avatar
        sx={{
          bgcolor: themeColors.primary,
          width: 54,
          height: 54,
          fontSize: '1.1rem',
          border: '2px solid rgba(255,255,255,0.2)',
        }}
      >
        {initials}
      </Avatar>
      <Box flex={1} width={'285px'}>
        {user?.firstName ?? user?.lastName ? (
          <Typography variant="subtitle1" fontWeight="600">
            {`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || ''}
          </Typography>
        ) : null}

        <Typography
          variant="body2"
          sx={{
            opacity: 0.8,
            fontSize: '0.8rem',
            display: '-webkit-box',
            WebkitLineClamp: 2, // limit to 2 lines
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            wordBreak: 'break-word', // ensures long words break
          }}
        >
          {user?.email}
        </Typography>

        {/* Compact row for time + tz */}
        <Box display="flex" flexDirection="row" gap={1} mt={0.5}>
          <Typography
            variant="caption"
            sx={{ opacity: 0.75, fontSize: '0.72rem' }}
          >
            {timeInfo.split('•')[0].trim()}
          </Typography>
          <Typography
            variant="caption"
            sx={{ opacity: 0.6, fontSize: '0.72rem' }}
          >
            {resolvedTimeZoneOffset}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const ThemeToggle = () => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const externalUserHandler = new ExternalUserHandler();
  /** Handle Click to update toggle */
  const handleToggleTheme = async () => {
    await externalUserHandler.updateUserTheme(
      enableDarkTheme ? 'light' : 'dark'
    );
  };

  return (
    <MenuItem
      onClick={handleToggleTheme}
      sx={{
        borderRadius: 2,
        py: 1,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        '&:hover': {
          backgroundColor: 'rgba(255,255,255,0.1)',
        },
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        {enableDarkTheme ? <Brightness7 /> : <Brightness4 />}
        <Typography fontWeight={500}>
          {enableDarkTheme ? t('dark_mode') : t('light_mode')}
        </Typography>
      </Box>
      <Chip
        size="small"
        label={enableDarkTheme ? t('switch_to_light') : t('switch_to_dark')}
        sx={{
          bgcolor: 'rgba(255,255,255,0.15)',
          color: 'white',
          fontSize: '0.7rem',
          fontWeight: 500,
        }}
      />
    </MenuItem>
  );
};

const ProductiveTripsToggle = () => {
  const { t } = useTranslation();
  const externalUserHandler = new ExternalUserHandler();
  const { showOnlyProductiveTrips, userReducerLocation, userId } =
    useShowOnlyProductiveTrips();
  const [showReloadDialog, setShowReloadDialog] = useState(false);

  const handleToggleProductiveTrips = async () => {
    try {
      await externalUserHandler.updateShowUnproductiveTripsPreference({
        showUnproductiveTrips: showOnlyProductiveTrips,
        userId,
        userReducerLocation,
      });
      setShowReloadDialog(true);
    } catch (error) {
      console.error('Error updating trip filter preference:', error);
    }
  };

  return (
    <>
      <MenuItem
        onClick={handleToggleProductiveTrips}
        sx={{
          borderRadius: 2,
          py: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.1)',
          },
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          {showOnlyProductiveTrips ? <AltRoute /> : <Merge />}
          <Typography fontWeight={500}>
            {showOnlyProductiveTrips ? t('productive_trips') : t('all_trips')}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={showOnlyProductiveTrips ? t('show_all') : t('show_productive')}
          sx={{
            bgcolor: 'rgba(255,255,255,0.15)',
            color: 'white',
            fontSize: '0.7rem',
            fontWeight: 500,
          }}
        />
      </MenuItem>
      <ReloadDialog
        open={showReloadDialog}
        message={t(
          'trip_filter_preference_has_been_updated_the_app_will_reload_'
        )}
        buttonText={t('reload_now')}
      />
    </>
  );
};

const LANGUAGE_OPTIONS: Array<{ value: SupportedLanguage; label: string }> = [
  { value: 'en', label: 'English' },
  { value: 'ru', label: 'Русский' },
  { value: 'es', label: 'Español' },
  // { value: 'fr', label: 'Français' }, // disabled for now — not needed yet
];

const LanguageToggle = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const language = useAppSelector((state) => state.languageReducer.language);
  const { enableDarkTheme } = useAppTheme();

  const handleChange = (e: SelectChangeEvent) => {
    dispatch(setLanguage(e.target.value as SupportedLanguage));
  };

  return (
    <MenuItem
      disableRipple
      sx={{
        borderRadius: 2,
        py: 1,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
        cursor: 'default',
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <Language />
        <Typography fontWeight={500}>{t('language_word')}</Typography>
      </Box>
      <Select
        size="small"
        value={language}
        onChange={handleChange}
        onClick={(e) => {
          e.stopPropagation();
        }}
        sx={{
          fontSize: '0.75rem',
          fontWeight: 500,
          color: enableDarkTheme ? 'white' : 'inherit',
          bgcolor: 'rgba(255,255,255,0.15)',
          borderRadius: 4,
          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
          '& .MuiSvgIcon-root': {
            color: enableDarkTheme ? 'white' : 'inherit',
          },
        }}
      >
        {LANGUAGE_OPTIONS.map(({ value, label }) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </Select>
    </MenuItem>
  );
};

const SwitchModuleButton = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  const customer = useAppSelector((state) => state.persistedReducer.customer);
  const role = useAppSelector((state) => state.authReducer.role);
  const navigate = useNavigate();

  const handleSwitchModule = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onClose();

    const modulesUrl =
      customer?.id && customer?.code
        ? `/modules?customerId=${customer.id}&customerCode=${customer.code}`
        : '/modules';

    // External users should open Modules in a new tab; others should switch in the current tab.
    if (role && isExternalRole(role)) {
      window.open(modulesUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    navigate(modulesUrl);
  };

  return (
    <MenuItem
      onClick={handleSwitchModule}
      sx={{
        borderRadius: 2,
        py: 1,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        '&:hover': {
          backgroundColor: 'rgba(255,255,255,0.1)',
        },
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <SwapHoriz />
        <Typography fontWeight={500}>{t('switch_module')}</Typography>
      </Box>
      <Chip
        size="small"
        label={t('go_home')}
        sx={{
          bgcolor: 'rgba(255,255,255,0.15)',
          color: 'white',
          fontSize: '0.7rem',
          fontWeight: 500,
        }}
      />
    </MenuItem>
  );
};

const LogoutButton = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleLogout = () => {
    // Instead of immediately clearing session and removing auth,
    // navigate to the logging out screen where those operations will be performed
    navigate('/logging-out');
  };
  return (
    <MenuItem
      onClick={handleLogout}
      sx={{
        borderRadius: 2,
        mt: 2,
        justifyContent: 'center',
        fontWeight: 600,
        color: 'rgba(255,255,255,0.9)',
        background: '#D32F2F',
        transition: 'all 0.2s ease',
        '&:hover': {
          background: '#C62828',
        },
      }}
    >
      <Logout fontSize="small" sx={{ mr: 1 }} />
      {t('dm_log_action_logout')}
    </MenuItem>
  );
};

const ProfileMenu = ({
  anchorEl,
  onClose,
  user,
  initials,
}: {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  user: User | undefined;
  initials: string;
}) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const colorMode = enableDarkTheme ? 'dark' : 'light';
  const themeColors = clientThemePalette[CLIENT_THEME][colorMode];
  const modules = useAppSelector((state) => state.authReducer.customer.modules);
  const isTPMSOnlyCustomer = isTpmsOnlyCustomer(modules);

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: 3,
          mt: 1,
          width: 380,
          p: 1.5,
          background: enableDarkTheme
            ? themeColors.navbarBg
            : themeColors.boxBackground,
          backdropFilter: 'blur(12px)',
          color: enableDarkTheme
            ? ThemePalette.typography.white
            : ThemePalette.typography.darkGrey,
          border: `1px solid ${themeColors.borderGrey}`,
          boxShadow: enableDarkTheme
            ? '0 8px 32px rgba(0,0,0,0.5)'
            : '0 4px 16px rgba(0,0,0,0.1)',
          animation: 'fadeIn 0.25s ease-out',
          '@keyframes fadeIn': {
            from: { opacity: 0, transform: 'translateY(-6px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      <UserInfoSection user={user} initials={initials} />
      <Box mb={2}>
        <Typography
          variant="caption"
          sx={{ px: 2, py: 0.5, opacity: 0.65, fontWeight: 500 }}
        >
          {t('profile_preferences')}
        </Typography>
      </Box>
      <ThemeToggle />
      {!isTPMSOnlyCustomer && <ProductiveTripsToggle />}
      <LanguageToggle />
      <SwitchModuleButton onClose={onClose} />
      <Divider
        sx={{
          borderColor: themeColors.borderGrey,
        }}
      />
      <LogoutButton />
    </Menu>
  );
};

const ProfileButton: React.FC = () => {
  const { t } = useTranslation();
  const currentUser = useCurrentUserInfo();
  const { enableDarkTheme } = useAppTheme();
  const colorMode = enableDarkTheme ? 'dark' : 'light';
  const themeColors = clientThemePalette[CLIENT_THEME][colorMode];
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  let initials = 'U'; // default fallback

  if (currentUser?.firstName && currentUser?.lastName) {
    initials =
      currentUser.firstName.charAt(0).toUpperCase() +
      currentUser.lastName.charAt(0).toUpperCase();
  } else if (currentUser?.firstName) {
    initials = currentUser.firstName.charAt(0).toUpperCase();
  } else if (currentUser?.email) {
    initials = currentUser.email.charAt(0).toUpperCase();
  }

  return (
    <>
      <Tooltip title={t('click_to_open')} arrow>
        <Avatar
          onClick={(e) => {
            setAnchorEl(e.currentTarget);
          }}
          sx={{
            fontSize: '0.9rem',
            color: 'white',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            outline: `2px solid ${themeColors.navbarBg}`,

            background: `linear-gradient(135deg, ${themeColors.primary}cc, ${themeColors.primary})`,
            '&:hover': {
              transform: 'scale(1.08)',
              background: `linear-gradient(135deg, ${themeColors.primary}, ${themeColors.primary}dd)`,
            },
          }}
        >
          {initials}
        </Avatar>
      </Tooltip>

      {anchorEl && (
        <ProfileMenu
          anchorEl={anchorEl}
          onClose={() => {
            setAnchorEl(null);
          }}
          user={currentUser}
          initials={initials}
        />
      )}
    </>
  );
};

export default ProfileButton;
